import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Button } from 'reactstrap'
import { steamActions } from '../../redux/actions'
import { steamService } from '../../services'
import MyRewardsErrorModal from '../Modal/MyRewardsErrorModal/MyRewardsErrorModal'
import MyRewardsSuccessModal from '../Modal/MyRewardsSuccessModal/MyRewardsSuccessModal'
import FormattedMessage from '../FormattedMessage/FormattedMessage'
import Loader from '../Loader/Loader'

import './SteamIntegration.scss'

const steamTicket = 'steam_v1'
const steamAuthTimeout = 30000

class SteamIntegration extends Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      steamError: false,
      steamErrorString: null,
      steamSuccess: false,
      redeemedLocally: false,
    }
  }

  componentDidMount() {
    const localStorage = window.localStorage.getItem(steamTicket)

    if (!localStorage) {
      this.setState({ loading: false })
      return
    }

    this.validateStoredSteamTicket(localStorage)
      .catch(() => steamService.removeUserTicket())
      .finally(() => this.setState({ loading: false }))
  }

  validateStoredSteamTicket = localStorage => {
    const { checkAuthenticated, saveAuthUser, user } = this.props

    return checkAuthenticated(user).then(() => saveAuthUser(localStorage))
  }

  onSteamAuth = () => {
    const { checkAuthenticated, removeAuthUser, saveAuthUser, user } = this.props

    this.setState({ loading: true })
    steamService.removeUserTicket()
    const steamWindow = window.open(
      steamService.buildLoginAuthUrl(),
      'Steam Oauth',
      'width=700,height=600'
    )

    if (!steamWindow) {
      this.setState({
        loading: false,
        steamError: true,
        steamErrorString: 'error.steam.account',
      })
      return
    }

    this.checkStorage(steamWindow)
      .then(localStorage => checkAuthenticated(user).then(() => saveAuthUser(localStorage)))
      .then(() => this.setState({ loading: false }))
      .catch(() => {
        this.setState(
          {
            loading: false,
            steamError: true,
            steamErrorString: 'error.steam.account',
          },
          () => removeAuthUser()
        )
      })
  }

  checkStorage = steamWindow =>
    new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        clearInterval(interval)
        reject(new Error('Steam authentication timed out'))
      }, steamAuthTimeout)

      const interval = setInterval(() => {
        const localStorage = window.localStorage.getItem(steamTicket)
        if (localStorage) {
          clearInterval(interval)
          clearTimeout(timeout)
          resolve(localStorage)
        } else if (steamWindow.closed) {
          clearInterval(interval)
          clearTimeout(timeout)
          reject(new Error('Steam authentication window closed'))
        }
      }, 1000)
    })

  activateGame = () => {
    const { steam, contentId, couponCode, user, activateGame, removeAuthUser } = this.props
    const { steamid, accessToken, authenticated } = steam
    const hasSteamAuth = authenticated && steamid && accessToken
    const userId = user.currentUser.id

    if (hasSteamAuth) {
      activateGame(userId, contentId, couponCode, steamid, accessToken)
        .then(() => {
          const {
            steam: { errorMessage },
          } = this.props

          if (errorMessage) {
            this.setState(
              {
                loading: false,
                steamError: true,
                steamErrorString: `error.steam.200${errorMessage.replace(/\s/g, '').toLowerCase()}`,
              },
              () => removeAuthUser()
            )
          } else {
            this.setState({ loading: false, steamSuccess: true, redeemedLocally: true })
          }
        })
        .catch(error => {
          if (error.errors) {
            this.setState({
              loading: false,
              steamError: true,
              steamErrorString: error.errors.message,
            })
          } else {
            this.setState({
              loading: false,
              steamError: true,
              steamErrorString: 'error.steam.account',
            })
          }
          removeAuthUser()
        })
    } else {
      this.setState({ loading: false, steamError: true })
    }
  }

  toggleSteamError = () => {
    this.setState(state => ({ steamError: !state.steamError }))
  }

  toggleSteamSuccess = () => {
    this.setState(state => ({ steamSuccess: !state.steamSuccess }))
  }

  steamLogout = () => {
    const { removeAuthUser } = this.props
    removeAuthUser()
  }

  renderSteamLogout = () => {
    const {
      steam: { authenticated, steamid, accessToken },
    } = this.props
    if (authenticated && steamid && accessToken) {
      return (
        <a
          className="logout"
          onClick={this.steamLogout}
          role="button"
          tabIndex={0}
          aria-label="Logout from Steam account"
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              this.steamLogout()
            }
          }}
        >
          <u>
            <FormattedMessage id="steam.button.logout" />
          </u>
        </a>
      )
    }
  }

  renderButton = () => {
    const {
      redeemed: redeemedFromProps,
      steam: { accessToken, authenticated, steamid },
    } = this.props
    const { redeemedLocally } = this.state
    const redeemed = redeemedFromProps || redeemedLocally
    const hasSteamAuth = authenticated && steamid && accessToken

    let buttonText = 'steam.button.logIn'
    let buttonClick = this.onSteamAuth
    let ariaLabel = 'Log in to Steam'

    if (redeemed) {
      buttonText = 'steam.button.play'
      buttonClick = () => {
        window.open('https://steamcommunity.com/login/home/')
      }
      ariaLabel = 'Open Steam Community (opens in new window)'
    } else if (hasSteamAuth) {
      buttonText = 'steam.button.activate'
      buttonClick = this.activateGame
      ariaLabel = 'Activate game on Steam'
    }

    return (
      <div className="col">
        <Button
          color="dark"
          block
          outline
          onClick={buttonClick}
          aria-describedby="steam-integration-status"
          aria-label={ariaLabel}
        >
          <FormattedMessage id={buttonText} />
        </Button>
        {this.renderSteamLogout()}
      </div>
    )
  }

  render() {
    const {
      contentName,
      redeemed: redeemedFromProps,
      steam: { accessToken, authenticated, steamid },
    } = this.props
    const { loading, steamError, steamSuccess, steamErrorString, redeemedLocally } = this.state
    const redeemed = redeemedFromProps || redeemedLocally
    const hasSteamAuth = authenticated && steamid && accessToken

    if (loading) {
      return <Loader button />
    }
    return (
      <div className="mb-3">
        <div className="row">{this.renderButton()}</div>
        <div id="steam-integration-status" className="sr-only">
          {(() => {
            if (redeemed) return 'Game activated successfully'
            if (hasSteamAuth) return 'Logged into Steam'
            return 'Not logged in'
          })()}
        </div>
        <MyRewardsErrorModal
          isOpen={steamError}
          toggle={this.toggleSteamError}
          error={steamErrorString}
          platform="steam"
        />
        <MyRewardsSuccessModal
          isOpen={steamSuccess}
          toggle={this.toggleSteamSuccess}
          contentName={contentName}
          platform="steam"
        />
      </div>
    )
  }
}

const mapStateToProps = state => ({
  steam: state.steamReducer,
  user: state.userReducers,
})

const mapDispatchToProps = dispatch => ({
  activateGame: (...args) => dispatch(steamActions.activateGame(...args)),
  checkAuthenticated: (...args) => dispatch(steamActions.checkAuthenticated(...args)),
  removeAuthUser: () => dispatch(steamActions.removeAuthUser()),
  saveAuthUser: (...args) => dispatch(steamActions.saveAuthUser(...args)),
})

export default connect(mapStateToProps, mapDispatchToProps)(SteamIntegration)

SteamIntegration.propTypes = {
  redeemed: PropTypes.bool,
  contentName: PropTypes.string,
  contentId: PropTypes.number,
  couponCode: PropTypes.string,
}
